import {authFetch} from '../../authentication/authFetch';

const API_URL = process.env.REACT_APP_API_URL;

function normalizeTopic(topic, index) {
    return {
        ...topic,
        status: topic?.status ?? 'NOT_STARTED',
        order: topic?.order ?? topic?.position ?? index + 1,
        estimatedMinutes: topic?.estimatedMinutes ?? topic?.estimated_minutes ?? 0,
        prerequisites: topic?.prerequisites ?? [],
        resources: topic?.resources ?? [],
        exercises: topic?.exercises ?? [],
        recallCards: topic?.recallCards ?? topic?.recall_cards ?? [],
        notes: topic?.notes ?? [],
    };
}

function normalizeModule(module, index) {
    return {
        ...module,
        order: module?.order ?? module?.position ?? index + 1,
        resources: module?.resources ?? [],
        notes: module?.notes ?? [],
        topics: (module?.topics ?? []).map(normalizeTopic),
    };
}

export function normalizeRoadmap(roadmap) {
    if (!roadmap) return null;
    return {
        ...roadmap,
        userId: roadmap.userId ?? roadmap.user_id ?? null,
        goal: roadmap.goal ?? roadmap.learningGoal ?? roadmap.learning_goal ?? '',
        deadline: roadmap.deadline ?? null,
        estimatedHours: roadmap.estimatedHours ?? roadmap.estimated_hours ?? 0,
        createdAt: roadmap.createdAt ?? roadmap.created_at ?? null,
        updatedAt: roadmap.updatedAt ?? roadmap.updated_at ?? null,
        modules: (roadmap.modules ?? []).map(normalizeModule),
    };
}

function serializeTopic(topic, index) {
    return {
        id: topic.id ?? null,
        title: topic.title ?? '',
        description: topic.description ?? '',
        status: topic.status ?? 'NOT_STARTED',
        order: topic.order ?? index + 1,
        estimatedMinutes: Number(topic.estimatedMinutes) || 0,
        prerequisites: topic.prerequisites ?? [],
        resources: topic.resources ?? [],
        exercises: topic.exercises ?? [],
        recallCards: topic.recallCards ?? [],
        notes: topic.notes ?? [],
    };
}

function serializeModule(module, index) {
    return {
        id: module.id ?? null,
        title: module.title ?? '',
        description: module.description ?? '',
        order: module.order ?? index + 1,
        resources: module.resources ?? [],
        notes: module.notes ?? [],
        topics: (module.topics ?? []).map(serializeTopic),
    };
}

function serializeRoadmap(roadmap) {
    return {
        title: roadmap.title ?? '',
        description: roadmap.description ?? '',
        goal: roadmap.goal ?? roadmap.learningGoal ?? '',
        difficulty: roadmap.difficulty ?? 'BEGINNER',
        estimatedHours: Number(roadmap.estimatedHours) || 0,
        deadline: roadmap.deadline || null,
        emoji: roadmap.emoji ?? '🧭',
        modules: (roadmap.modules ?? []).map(serializeModule),
    };
}

function serializeGenerationRequest(request) {
    return {
        title: request.title?.trim() ?? '',
        learningGoal: request.learningGoal?.trim() ?? '',
        description: request.description?.trim() ?? '',
        difficulty: request.difficulty ?? 'BEGINNER',
        estimatedHours: Number(request.estimatedHours) || 0,
        deadline: request.deadline || null,
    };
}

async function parseError(response, fallback) {
    const payload = await response.json().catch(() => null);
    throw new Error(payload?.detail || payload?.message || fallback);
}

export async function fetchRoadmaps(token) {
    if (!token) return [];
    const response = await authFetch(`${API_URL}/api/roadmaps`, {headers: {'Content-Type': 'application/json'}});
    if (!response.ok) return parseError(response, 'Failed to fetch roadmaps');
    const payload = await response.json();
    return (Array.isArray(payload) ? payload : []).map(normalizeRoadmap).filter(Boolean);
}

export async function fetchRoadmap(token, roadmapId) {
    if (!token || !roadmapId) return null;
    const response = await authFetch(`${API_URL}/api/roadmap/${roadmapId}`, {headers: {'Content-Type': 'application/json'}});
    if (!response.ok) return parseError(response, 'Failed to fetch roadmap');
    return normalizeRoadmap(await response.json());
}

export async function createRoadmap(token, roadmap) {
    if (!token) throw new Error('Missing auth token');
    const response = await authFetch(`${API_URL}/api/roadmap`, {
        method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify(serializeRoadmap(roadmap)),
    });
    if (!response.ok) return parseError(response, 'Failed to create roadmap');
    return normalizeRoadmap(await response.json());
}

export async function generateRoadmapPreview(token, request) {
    if (!token) throw new Error('Missing auth token');
    const response = await authFetch(`${API_URL}/api/roadmap/generate`, {
        method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify(serializeGenerationRequest(request)),
    });
    if (!response.ok) return parseError(response, 'Failed to generate roadmap');
    return normalizeRoadmap(await response.json());
}

export async function updateRoadmap(token, roadmapId, roadmap) {
    if (!token || !roadmapId) throw new Error('Missing roadmap id or auth token');
    const response = await authFetch(`${API_URL}/api/roadmap/${roadmapId}`, {
        method: 'PUT', headers: {'Content-Type': 'application/json'}, body: JSON.stringify(serializeRoadmap(roadmap)),
    });
    if (!response.ok) return parseError(response, 'Failed to update roadmap');
    return normalizeRoadmap(await response.json());
}

export async function deleteRoadmap(token, roadmapId) {
    if (!token || !roadmapId) throw new Error('Missing roadmap id or auth token');
    const response = await authFetch(`${API_URL}/api/roadmap/${roadmapId}`, {method: 'DELETE'});
    if (!response.ok) return parseError(response, 'Failed to delete roadmap');
    return response.json();
}
